import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'

Vue.use(Vuex)

const updateLocations = (state) => {
  const locations = state.widget?.settings?.locations || []
  if (locations && locations.length) {
    state.locations = locations
    if (state.widget?.settings?.show_location_dropdown) {
      if (locations.length === 1) {
        state.selectedAccountId = locations[0]._id
      } else {
        state.selectedAccountId = undefined
      }
    }
  }
}

export default (storeProps = {}) => {
  const store = new Vuex.Store({
    state: {
      apiUrl: '',
      preview: false,
      initialLoading: true,
      loading: false,
      fatalError: null, // A permanent error that prevents the widget from continuing to function.
      error: null,
      page: null,
      showTerms: null,
      authChannel: null, // Can be set to SHOPIFY to cause the widget to be compatible with Shopify auth
      token: null, // Auth token that is used for widget endpoints that contains a contact_id and account_id
      contact: null,
      events: [], // Events related to the contact
      schemas: null,
      widget: null,
      widgetId: null, // Overridden locally for dev so widget can be pulled by id
      account: null,
      locations: [], // list of {_id,name}
      selectedAccountId: undefined, // currently selected account/location
      messageText: null, // Going to handle this better in the future
      ...(storeProps.state || {})
    },
    getters: {
      widgetSettings (state) {
        return state.widget?.settings || {}
      },
      color (state, getters) {
        return getters.widgetSettings.color || '#888'
      },
      termsVisible (state, getters) {
        return state.showTerms || getters.requireAcceptTerms
      },
      bannerTitle (state, getters) {
        if (getters.termsVisible) {
          return 'TERMS & CONDITIONS'
        }
        if (state.page) {
          return {
          }[state.page] || getters.widgetSettings?.banner_title || getters.widgetSettings?.banner_text || 'TEXTCHAT' // TODO: Rename this field to banner_title so LoyaltyCash and TextChat are consistent
        }
        return getters.widgetSettings?.banner_title || getters.widgetSettings?.banner_text || 'TEXTCHAT' // TODO: Rename this field to banner_title so LoyaltyCash and TextChat are consistent
      },
      ...(storeProps.getters || {})
    },
    mutations: {
      setInitialLoading (state, value) {
        state.initialLoading = value
      },
      setLoading (state, value) {
        state.loading = value
      },
      setFatalError (state, value) {
        state.fatalError = value
      },
      setError (state, value) {
        state.error = value
      },
      setContact (state, value) {
        state.contact = value
      },
      setEvents (state, value) {
        state.events = value
      },
      setToken (state, value) {
        state.token = value
      },
      setWidget (state, value) {
        state.widget = value
        updateLocations(state)
      },
      setAccount (state, value) {
        state.account = value
      },
      setLocations (state) {
        updateLocations(state)
      },
      setSelectedAccountId (state, value) {
        state.selectedAccountId = value
      },
      setSchemas (state, value) {
        state.schemas = value
      },
      setShowTerms (state, value) {
        state.showTerms = value
      },
      setPage (state, value) {
        state.page = value
        state.error = null
      },
      setMessageText (state, value) {
        state.messageText = value
      },
      ...(storeProps.mutations || {})
    },
    actions: {
      async initialize ({ state, commit }) {
        // Pull widget config
        if (!state.widget && state.widgetId) {
          commit('setInitialLoading', true)
          try {
            const widgetResult = await Vue.$http({
              method: 'GET',
              url: `/v2/widgets/${state.widgetId}/public`
            })
            if (widgetResult.data.widget) {
              commit('setWidget', widgetResult.data.widget)
            }
            if (widgetResult.data.account) {
              commit('setAccount', widgetResult.data.account)
            }
            commit('setLocations')
            if (widgetResult.data.schemas) {
              commit('setSchemas', widgetResult.data.schemas)
            }
          } catch (e) {
            commit('setFatalError', 'Could not load settings: ' + (e.response?.data?.error || e.message))
          } finally {
            commit('setInitialLoading', false)
          }
        } else {
          commit('setLocations')
          commit('setInitialLoading', false)
        }
      },
      async getContact ({ state, commit }, token) {
        if (token) {
          commit('setToken', token)
        }
        if (!state.widget?._id || !state.account?._id || !state.token) {
          commit('setInitialLoading', false)
          return
        }
        // Pull the fresh contact if auth token exists
        commit('setInitialLoading', true)
        try {
          const result = await Vue.$http({
            method: 'GET',
            url: `/v2/widgets/${state.widget._id}/contact`
          })
          if (result?.data?.data) {
            commit('setContact', result.data.data)
          }
          if (result?.data?.relationships?.events) {
            commit('setEvents', result.data.relationships.events)
          }
        } finally {
          commit('setInitialLoading', false)
        }
      },
      async submit ({ state, commit }, data) {
        // Do nothing if we're in preview mode
        // if (state.preview) {
        //   return
        // }
        // Pull the fresh contact if auth token exists
        commit('setLoading', true)
        commit('setError', null)
        try {
          await Vue.$http({
            method: 'POST',
            url: `/v2/widgets/${state.widget._id}/public`,
            data
          })
          commit('setMessageText', data.message)
          commit('setPage', 'done')
        } finally {
          commit('setLoading', false)
        }
      },
      ...(storeProps.actions || {})
    }
  })

  const axiosInstance = axios.create({
    baseURL: store.state.apiUrl
  })

  axiosInstance.interceptors.request.use((config) => {
    const skipHeaders = (config?.skipHeaders || []).map(h => ((h || '') + '').toLowerCase())
    config.headers = {
      ...(config.headers || {}),
      'x-client-id': process?.env?.VUE_APP_CLIENT_ID,
      'x-client-version': process?.env?.VUE_APP_CLIENT_VERSION
    }
    const headerAccountId = store.state.selectedAccountId || store.state.account?._id
    if (headerAccountId && !skipHeaders.includes('x-account-id') && [undefined, null].includes(config.headers['x-account-id'])) {
      config.headers['x-account-id'] = headerAccountId + ''
    }
    if (store.state.token) {
      config.headers['x-authorization'] = `${store.state.token}`
    }
    return config
  })
  axiosInstance.interceptors.response.use((response) => {
    return response
  }, (error) => {
    // Ignore errors resulting from cancelled requests
    if (error.message.includes('request cancelled') || (error.constructor && error.constructor.name === 'Cancel')) {
      throw error // The component triggering this should handle the error
    }
    console.error({ error })
    let message = error.message
    if (error.response) {
      message = error.response?.data?.error || error.response?.data || message
    }
    if (!(error.config && error.config.hideErrors) && store) {
      if ((error.response?.status || '') + '' === '401') {
        store.commit('setContact', null)
        store.commit('setPage', null)
      }
      store.commit('setError', message)
    }
    throw error
  })

  axiosInstance.CancelToken = axios.CancelToken
  Vue.prototype.$http = Vue.$http = axiosInstance

  window.$store = store

  return store
}
